import React, { useCallback, useMemo } from 'react';

import { Box, Tooltip } from '@chakra-ui/react';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { capitalize, startCase } from 'lodash';
import { Collaborators } from 'screens/Team/Rabbit/Goals/AgGridTreeView/components/CommonTreeCell/Collabs';
import { ObjectiveKRCell } from 'screens/Team/Rabbit/Goals/AgGridTreeView/components/CommonTreeCell/ObjectiveKRCell';
import { Owners } from 'screens/Team/Rabbit/Goals/AgGridTreeView/components/CommonTreeCell/Owners';
import { OwnersAndCollaborators } from 'screens/Team/Rabbit/Goals/AgGridTreeView/components/CommonTreeCell/OwnersAndCollaborators';
import { CustomHeaderCell } from 'screens/Team/Rabbit/Goals/AgGridTreeView/components/CustomHeaderCell';
import { postProcessPopup } from 'screens/Team/Rabbit/Goals/AgGridTreeView/utils';

import { OBJECTIVE_LEVEL_TAGS } from 'constants/ProjectConstants';
import { StringModifier, modifyString } from 'constants/copyHelpers';

import { AgGridWrapper } from 'uiToolkitV1/AgGridWrapper';

import RadioButton from './RadioButton';

interface Props {
  gridData: any;
  gridRef: any;
  selectedId: string;
  onSelect?: (goal: Record<string, any>) => void;
}

const SetFilterRenderer = (params) => (
  <Tooltip label={params?.value ?? ''}>
    <p>{params?.value ?? '(Blanks)'}</p>
  </Tooltip>
);

const RadioCellRenderer =
  ({ onSelect, selectedId }) =>
  ({ data }) => {
    if (data.type === 'GOAL') {
      return <RadioButton rowData={data} onSelect={onSelect} isDisabled={false} selectedId={selectedId} />;
    }
    return null;
  };

const GoalSectionAgGrid = ({ gridData, gridRef, onSelect, selectedId }: Props) => {
  const { moduleName } = useOrganisationSettings();

  const columnDefs = useMemo(
    () => [
      // we're using the auto group column by default!
      {
        field: 'toIds',
        filter: 'agMultiColumnFilter',
        sortable: true,
        headerName: 'Team',
        minWidth: 100,
        filterParams: {
          filters: [
            {
              filter: 'agSetColumnFilter',
              display: 'accordion',
              title: 'Filter by Function',
              filterParams: {
                valueFormatter: ({ value }) => {
                  if (value) return value;
                  return '';
                },
                getValue: (data) => data.data?.toIds?.map(({ employeeFunction }) => employeeFunction)?.filter((v) => v),
                cellRenderer: SetFilterRenderer,
              },
            },
            {
              filter: 'agSetColumnFilter',
              display: 'accordion',
              title: 'Filter by Name',
              filterParams: {
                applyMiniFilterWhileTyping: true,
                valueFormatter: ({ value }) => {
                  if (value) return value;
                  return '(Blanks)';
                },
              },
            },
          ],
        },
        valueGetter: (data) => data.data?.toIds?.map(({ displayName }) => displayName),
        cellRenderer: OwnersAndCollaborators,
      },
      {
        field: 'toIds',
        filter: 'agMultiColumnFilter',
        hide: true,
        sortable: true,
        minWidth: 140,
        maxWidth: 280,
        headerName: 'Owner',
        filterParams: {
          filters: [
            {
              filter: 'agSetColumnFilter',
              display: 'accordion',
              title: 'Filter by Function',
              filterParams: {
                valueFormatter: ({ value }) => {
                  if (value) return value;
                  return '';
                },
                getValue: (data) =>
                  data.data?.toIds
                    ?.map(({ employeeFunction, ownerType }) => ownerType === 'OWNER' && employeeFunction)
                    ?.filter((v) => v),
                cellRenderer: SetFilterRenderer,
              },
            },
            {
              filter: 'agSetColumnFilter',
            },
          ],
        },
        valueGetter: (data) => data.data?.toIds?.map(({ displayName }) => displayName),
        cellRenderer: Owners,
      },
      {
        field: 'toIds',
        filter: 'agMultiColumnFilter',
        hide: true,
        sortable: true,
        headerName: 'Collaborators',
        minWidth: 160,
        maxWidth: 220,
        filterParams: {
          filters: [
            {
              filter: 'agSetColumnFilter',
              display: 'accordion',
              title: 'Filter by Function',
              filterParams: {
                valueFormatter: ({ value }) => {
                  if (value) return value;
                  return '';
                },
                getValue: (data) =>
                  data.data?.toIds
                    ?.map(({ employeeFunction, ownerType }) => ownerType === 'COLLABORATOR' && employeeFunction)
                    ?.filter((v) => v),
                cellRenderer: SetFilterRenderer,
              },
            },
            {
              filter: 'agSetColumnFilter',
              filterParams: {
                valueFormatter: ({ value }) => {
                  if (value) return value;
                  return '(Blanks)';
                },
              },
            },
          ],
        },
        valueGetter: (data) =>
          data.data?.toIds?.map(({ displayName, ownerType }) => ownerType === 'COLLABORATOR' && displayName),
        cellRenderer: Collaborators,
      },

      {
        headerName: '',
        minWidth: 36,
        maxWidth: 36,
        cellRenderer: RadioCellRenderer({ onSelect, selectedId }),
        lockVisible: true,
        lockPosition: 'left',
        suppressMovable: true,
        suppressFiltersToolPanel: true,
        suppressHeaderMenuButton: true,
        suppressColumnsToolPanel: true,
      },
    ],
    [selectedId]
  );

  const autoGroupColumnDef = useMemo(
    () => ({
      headerName: modifyString(moduleName, StringModifier.PLURAL_TITLE_CASE),
      minWidth: 350,
      maxWidth: 350,
      flex: 1,
      filter: 'agMultiColumnFilter',
      filterParams: {
        filters: [
          {
            filter: 'agSetColumnFilter',
            display: 'accordion',
            title: 'Filter by Level',
            filterParams: {
              values: OBJECTIVE_LEVEL_TAGS.map(({ value }) => value),
              // this is used to display the value in the filter menu
              valueFormatter: ({ value }) => capitalize(startCase(value)),
              // this is used to get the key which is used to filter the data
              getValue: (data) => data.data.objectiveType,
            },
          },
          {
            getValue: (data) => data.data.title,
            filterParams: {
              // copy pasta from ag grid.
              // not touching it for now
              textFormatter: (r) => {
                if (r === null) return null;
                return r
                  .toLowerCase()
                  .replace(/[àáâãäå]/g, 'a')
                  .replace(/æ/g, 'ae')
                  .replace(/ç/g, 'c')
                  .replace(/[èéêë]/g, 'e')
                  .replace(/[ìíîï]/g, 'i')
                  .replace(/ñ/g, 'n')
                  .replace(/[òóôõö]/g, 'o')
                  .replace(/œ/g, 'oe')
                  .replace(/[ùúûü]/g, 'u')
                  .replace(/[ýÿ]/g, 'y');
              },
              filterOptions: ['contains', 'notContains'],
              debounceMs: 200,
              suppressAndOrCondition: true,
            },
          },
        ],
      },
      valueGetter: (data) => data.data.title,
      cellRendererParams: {
        suppressCount: true,
        innerRenderer: ObjectiveKRCell,
      },
      cellClass: 'objective-kr-cell-ag-grid-wrap',
      autoHeight: true,
      wrapText: true,
    }),
    [moduleName]
  );

  const onGridReady = useCallback(() => {
    gridRef?.current?.api?.collapseAll();
  }, [gridRef]);

  const defaultColDef = useMemo(
    () => ({
      flex: 0,
      menuTabs: ['filterMenuTab'],
      headerComponent: CustomHeaderCell,
    }),
    []
  );

  const getDataPath = useMemo(() => (data) => data.hierarchy, []);

  const getRowId = useCallback(({ data }) => data?.uuid, []);

  return (
    <Box className="ag-theme-alpine" width="100%" flex={1} px={2}>
      <AgGridWrapper
        useFilterModelPersistence={false}
        useColumnStatePersistence={false}
        ref={gridRef}
        rowData={gridData}
        // @ts-ignore
        columnDefs={columnDefs}
        // @ts-ignore
        defaultColDef={defaultColDef}
        autoGroupColumnDef={autoGroupColumnDef}
        treeData
        animateRows
        groupDefaultExpanded={-1}
        getDataPath={getDataPath}
        style={{ width: '100%', height: '100%' }}
        domLayout="normal"
        rowHeight={74}
        rowStyle={{ cursor: 'pointer' }}
        getRowId={getRowId}
        suppressMenuHide
        postProcessPopup={postProcessPopup}
        onRowDataUpdated={onGridReady}
      />
    </Box>
  );
};

export default GoalSectionAgGrid;
